import { Control } from '../control';
import { Automation } from '../automation';
import { View } from '../view';
import { Form } from '../form';
import { Filter } from '../filter';
export namespace SmartAction {
    // 支持操作列的视图类型
    export const SupportOperationColumnViewModes = [View.ViewMode.List, View.ViewMode.Card];
    // 按钮执行类型
    export enum ExecuteType {
        Unspecified,
        Automation = 1, // 自动化
        OnlyCustomCode = 2, // 自定义代码
        OnlyCustomCode_List = 21,
        OnlyCustomCode_Form = 22,
        Plugin = 3, // 插件
        CreateBizObject = 4, // 新增表单
        UpdatePropertyValue = 5, // 批量编辑
        OpenLink = 6, // 打开链接
        OpenDashboard = 7, // 打开仪表盘
        NewCustomCode = 8, // 新版自定义代码
        MergeCreateBO = 9, // 合并新增
        CallAgent = 10, // 调用智能体
    }

    // 按钮样式
    export enum StyleType {
        Primary = 'primary', // 主按钮
        Danger = 'danger',
        Default = 'default',
    }

    // 默认值设置值类型
    export enum DefaultValueType {
        Fixed = 1,
        Property = 2,
        Expression = 3,
        CustomParameter = 4,
        Max = 100,
        Min = 101,
        Sum = 102,
        Sum_Distinct = 103, // 合并
        Split = 104, // 分割
        ExternalFormLink = 105,
        DataSharingLink = 106,
        PublicQueryLink = 107,
        SetEmpty_IfAllsSame = 108,
    }

    export interface IButtonConfig {
        code: string;
        displayName: string;
        icon?: string;
    }

    // 值类型
    export enum ValueType {
        PropertyName = 'PropertyName',
        Fixed = 'Fixed',
        Expression = 'Expression',
        Custom = 'Custom', // 自定义
        Dynamic = 'Dynamic',
        CustomParameter = 'CustomParameter',
    }

    // 操作类型
    export enum ValueSetType {
        Link = -1, // 帮助链接
        Set = 1, // 设为
        Append = 2, // 追加
        Clear = 3, // 清空
        Remove = 4, // 移除
    }
    // 字段映射
    export interface IPropertyMapping {
        schemaCode?: string;
        propertyName: string;
        valueType: DefaultValueType;
        value: string;
        valueSetType: ValueSetType;
        disabled?: boolean;
        removable?: boolean;
    }

    // 打开链接目标类型
    export enum OpenLinkType {
        Node,
        Self,
        Blank,
    }

    export interface ILinkParamItem {
        key: string; // 参数key
        dataType: Control.BizDataType; // 参数数据类型
        valueType: DefaultValueType; // 参数类型
        value: string; // 参数值
    }

    export interface IOpenLinkSetting {
        url: string;
        openLinkType: OpenLinkType;
        isOpenLinkParamSettingEnabled?: boolean;
        openLinkParamSetting?: {
            controls: ILinkParamItem[];
        };
    }

    export interface IOpenDashboardSetting {
        dashboardId: string;
        chartIds: string[];
    }

    export interface ICustomArgControl {
        key: string;
        dataType: number;
        displayName: string;
        controlType: Control.FormControlKeys;
        visible: boolean;
        required: boolean;
        extension: {
            DefaultItems?: string[];
            DefaultValue?: string;
            DisplayFormat?: string;
            DecimalPlaces?: number;
            ShowMode?: number;
            Mode?: string;
            UnitSelectionRange?: string;
            AssociationSchemaCode?: string;
            BOSchemaInfo?: string;
            PropertyMappings?: IPropertyMapping[];
        };
    }

    // 按钮执行场景
    export enum ActionScene {
        Unspecified = 0,
        Form = 1, // 表单
        Toolbar = 2, // 工具栏
        OperationColumn = 3, // 操作列
    }

    // 提交规则操作类型
    export enum SubmitRuleActionType {
        None = 0,
        ShowErrorMessage = 1, // 未满足校验隐藏时，提示错误信息
        HideButton = 2, // 未满足校验禁用时，隐藏按钮
    }

    export interface IAutomationParamSetting {
        controls: Automation.V3.ICustomArgControl[];
    }
    export interface IExecuteSetting {
        executeType: ExecuteType; // 按钮执行类型
        editableProperties?: string[]; // 批量编辑的字段（批量编辑时使用）
        triggerFormRule?: boolean; // 是否触发表单规则（批量编辑时使用）
        automationTemplateId?: string; // 自动执行的模板id（自动执行时使用）
        automationParamSetting?: IAutomationParamSetting; // 自动化参数设置
        associationSchemaCode?: string; // 关联的表单code（新增打开表单时使用）
        propertyMappings?: IPropertyMapping[]; // 新增携带默认值字段设置（新增打开表单时使用）
        openLinkSetting?: IOpenLinkSetting; // 打开链接设置（打开链接时使用）
        openDashboardSetting?: IOpenDashboardSetting; // 打开仪表盘设置（打开仪表盘时使用）
        buildBizObjectSettingContainer?: {
            main: ICreateBizObjectSetting;
            children: ICreateBizObjectSetting[];
        };
        agentCode?: string;
    }

    // 一致性校验配置
    export interface IValidateFieldConsistency {
        state: boolean; // 启用/禁用
        properties: string[]; // 校验字段集合
    }
    export interface IAction {
        schemaCode: string;
        code: string;
        oldCode?: string; // 历史数据升级，旧编码，解决历史数据按钮编码重复的问题，旧自定义代码类型按钮执行时传这个编码
        displayName: string;
        icon: string | undefined;
        style: StyleType | undefined;
        confirmInfo?: {
            title: string;
            detail: string;
        } | null;
        submitRule?: {
            ruleString: string;
            errorMessage: string;
            state: boolean; // 启用/禁用
            submitRuleActionType: SubmitRuleActionType;
        };
        executeSetting?: IExecuteSetting;
        sortKey?: number;
        mergeBizData: boolean;
        selectable: boolean;
        behindCode?: string;
        frontendCode?: string;
        modifiedTime?: string;
        createdTime?: string;
        validateFieldConsistency?: IValidateFieldConsistency;
    }

    export interface ICreateBizObjectSetting {
        targetSchemaCode: string;
        sourceSchemaCode?: string;
        sourceCondition?: string;
        sourceConditionV1?: Filter.IFilterItem[][]; // 筛选条件
        propertyMappings?: IPropertyMapping[]; // 新增携带默认值字段设置（新增打开表单时使用）
        groupBy?: boolean; // 是否合并分组
    }

    // 视图支持的操作按钮集合
    export interface IOperationColumn {
        smartActionCodes: string[];
    }

    // 按钮操作列配置（按钮出现在哪些页面和页面内的哪些视图）
    export type TOperationColumnConfig = {
        key: string; // 页面Id
        value: View.ViewMode[]; // 视图类型Id集合，目前只支持表格视图和卡片视图
    };

    /** 系统按钮编码 */
    export const SystemActionCodes = [
        View.SystemActionKey.Remove,
        View.SystemActionKey.Remove_Form,
        View.SystemActionKey.Remove_List,
        View.SystemActionKey.Print,
        View.SystemActionKey.Print_Form,
        View.SystemActionKey.Print_List,
        View.SystemActionKey.Save,
        View.SystemActionKey.Submit,
        View.SystemActionKey.Copy,
        View.SystemActionKey.Create,
        View.SystemActionKey.Import,
        View.SystemActionKey.Export,
        View.SystemActionKey.PrintQrCode,
        View.SystemActionKey.PrintQrCode_Inner,
        View.SystemActionKey.PrintQrCode_Outer,
        View.SystemActionKey.Edit,
    ];

    export namespace Request {
        // 获取按钮列表请求参数
        export type TGetSmartActionParams = {
            schemaCode: string;
            mergeBizData?: boolean;
            selectable?: boolean;
            actionCodes?: string[];
        };

        // 保存按钮请求参数
        export type TSaveSmartActionParams = {
            smartAction: IAction;
        };

        // 删除按钮请求参数
        export type TRemoveSmartActionParams = {
            schemaCode: string;
            actionCode: string;
        };

        // 表单视图获取按钮列表请求参数
        export type TFormViewSmartActionGetParams = {
            schemaCode: string;
        };

        // 表单视图保存按钮列表请求参数
        export type TFormViewSmartActionSaveParams = {
            schemaCode: string;
            actionCodes: string[];
        };

        // 按钮场景配置请求参数（设置按钮在哪些场景可用）
        export type TSmartActionSceneSettingParams = {
            schemaCode: string;
            actionCode: string;
            viewPageIds: string[];
            formEnable: boolean; // 按钮是否可以在表单中使用
            operationColumns: TOperationColumnConfig[];
        };

        // 按钮执行请求参数
        export type TActionExecuteParams = {
            SchemaCode: string;
            ActionCode: string;
            ActionEnvironment: ActionScene;
            PostValue?: {
                Data: Record<string, unknown>;
            };
            SubmitFormRequest?: TSubmitFormRequest;
            ListViewRequest?: TListViewRequest;
        };

        // 表单场景下按钮执行请求参数
        export type TSubmitFormRequest = {
            ActionName: string;
            SchemaCode: string;
            BizObjectId: string;
            WorkflowVersion: number;
            WorkflowInstanceId: string;
            WorkItemId: string;
            Mode: Form.FormMode;
            IsExternalForm: boolean;
            IsExternalShare: boolean;
            PostValue: {
                Data: Record<string, string>;
            };
            BoSchemaCode?: string;
            BackwardComment?: string;
            CustomData?: Record<string, unknown>;
            Param?: string;
            RequestId?: string;
        };

        export type TListViewRequest = {
            // ViewPageId: string;
            ObjectIds: string[];
            QueryCode: string;
            // IsMobile: boolean;
        };
    }

    export namespace Response {
        export type TSmartActionResponse = {
            smartAction: IAction;
            viewPageIds: string[]; // 按钮可以出现的视图页面id集合，默认视图页面没有id，为''
            formEnable: boolean; // 按钮是否可以在表单中使用
            operationColumns: TOperationColumnConfig[]; // 按钮操作列配置
            roleIds?: string[]; // 按钮可以出现的角色id集合
            roleNames?: string[];
            roles?: Array<{ id: string; displayName: string }>; // 按钮可以出现的角色id集合
        };
        export type TSmartActionGetListResponse = {
            smartActions: TSmartActionResponse[];
        };
    }
}
